<script setup>
import AttentionQueueBadge from 'dashboard/attention/AttentionQueueBadge.vue';
// InteliChat sidebar: brand, workspace nav, Tools group, account
// switcher + agent identity block. All state comes through the
// intelichat adapters (native Vuex underneath, never accessed raw).
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import { useInteliAccount } from '../composables/useInteliAccount';
import { useInteliAgent } from '../composables/useInteliAgent';
import { useInteliConversation } from '../composables/useInteliConversation';
import logoFull from '../brand/intelichat-logo-light-trim.png';
// dark-theme wordmark (white lettering) - swapped in via CSS when the
// native `body.dark` theme class is active, so the brand stays legible
// in both themes (section 7 theme completeness).
import logoDark from '../brand/intelichat-logo-transparent-trim.png';
import logoIcon from '../brand/intelichat-icon-bubble.png';

const route = useRoute();
const router = useRouter();
const {
  accountId,
  accountName,
  sortedAccounts,
  hasMultipleAccounts,
  switchAccount,
} = useInteliAccount();
const { displayName, initials, availabilityLabel, availabilityTone } =
  useInteliAgent();
const { mineCount } = useInteliConversation();

const WORKSPACES = [
  { key: 'today', label: 'Today', routeName: 'intelichat_today' },
  { key: 'inbox', label: 'Inbox', routeName: 'intelichat_inbox' },
  // all-chats lookup: find + open ANY previous conversation (native
  // full search mounted in-shell, every status incl resolved)
  { key: 'history', label: 'History', routeName: 'intelichat_history' },
  { key: 'customers', label: 'Customers', routeName: 'intelichat_customers' },
  { key: 'orders', label: 'Orders', routeName: 'intelichat_orders' },
];

const TOOLS = [
  { key: 'tickets', label: 'Tickets', routeName: 'intelichat_tickets' },
  { key: 'fitment', label: 'Fitment', routeName: 'intelichat_fitment' },
  { key: 'recovery', label: 'Recovery', routeName: 'intelichat_recovery' },
  { key: 'analytics', label: 'Analytics', routeName: 'intelichat_analytics' },
];

// Native Chatwoot sections. The InteliChat chrome is persistent
// (Dashboard.vue renders it on EVERY route), so these open the native
// pages INSIDE the IC content area - the agent never sees raw native.
// Settings/Reports section navigation natively lives in the (hidden)
// NextSidebar, so the IC rail provides its own contextual sub-nav,
// expanded while that section is active.
const SETTINGS_SUBNAV = [
  {
    label: 'General',
    routeName: 'general_settings_index',
    match: '/settings/general',
  },
  { label: 'Agents', routeName: 'agent_list', match: '/settings/agents' },
  {
    label: 'Teams',
    routeName: 'settings_teams_list',
    match: '/settings/teams',
  },
  {
    label: 'Inboxes',
    routeName: 'settings_inbox_list',
    match: '/settings/inboxes',
  },
  { label: 'Labels', routeName: 'labels_list', match: '/settings/labels' },
  {
    label: 'Custom Attributes',
    routeName: 'attributes_list',
    match: '/settings/custom-attributes',
  },
  {
    label: 'Automation',
    routeName: 'automation_list',
    match: '/settings/automation',
  },
  { label: 'Macros', routeName: 'macros_wrapper', match: '/settings/macros' },
  {
    label: 'Canned Responses',
    routeName: 'canned_list',
    match: '/settings/canned-response',
  },
  {
    label: 'Integrations',
    routeName: 'settings_applications',
    match: '/settings/integrations',
  },
  {
    label: 'Audit Logs',
    routeName: 'auditlogs_list',
    match: '/settings/audit-logs',
  },
  {
    label: 'Custom Roles',
    routeName: 'custom_roles_list',
    match: '/settings/custom-roles',
  },
  { label: 'SLA', routeName: 'sla_list', match: '/settings/sla' },
  {
    label: 'Billing',
    routeName: 'billing_settings_index',
    match: '/settings/billing',
  },
  { label: 'Profile', routeName: 'profile_settings_index', match: '/profile' },
];

const REPORTS_SUBNAV = [
  {
    label: 'Overview',
    routeName: 'account_overview_reports',
    match: '/reports/overview',
  },
  {
    label: 'Conversation',
    routeName: 'conversation_reports',
    match: '/reports/conversation',
  },
  { label: 'CSAT', routeName: 'csat_reports', match: '/reports/csat' },
  { label: 'Agent', routeName: 'agent_reports', match: '/reports/agent' },
  { label: 'Label', routeName: 'label_reports', match: '/reports/label' },
  { label: 'Inbox', routeName: 'inbox_reports', match: '/reports/inboxes' },
  { label: 'Team', routeName: 'team_reports', match: '/reports/teams' },
  { label: 'SLA', routeName: 'sla_reports', match: '/reports/sla' },
];

const NATIVE_SECTIONS = [
  {
    key: 'contacts',
    label: 'Contacts',
    routeName: 'contacts_dashboard_index',
    path: '/contacts',
  },
  {
    key: 'reports',
    label: 'Reports',
    routeName: 'account_overview_reports',
    path: '/reports',
    children: REPORTS_SUBNAV,
  },
  {
    key: 'notifications',
    label: 'Notifications',
    routeName: 'notifications_index',
    path: '/notifications',
  },
  {
    key: 'settings',
    label: 'Settings',
    routeName: 'settings_home',
    path: '/settings',
    children: SETTINGS_SUBNAV,
  },
];

const isActive = key => route.meta?.inteliNav === key;

// native sections carry no inteliNav meta - match by path segment
const isNativeActive = item =>
  !route.meta?.inteliNav &&
  (route.path.includes(item.path) ||
    (item.children || []).some(c => route.path.includes(c.match)));

const isSubActive = c => route.path.includes(c.match);

const goTo = routeName => {
  router.push({ name: routeName, params: { accountId: accountId.value } });
};

const goToNative = item => {
  router.push({
    name: item.routeName,
    params: { accountId: accountId.value },
  });
};

const openNative = () => {
  // explicit Classic-view bypass: ?classic=1 flips the persistent
  // chrome back to native (sticky until an InteliChat route is opened)
  router.push({
    name: 'home',
    params: { accountId: accountId.value },
    query: { classic: '1' },
  });
};

// account switcher dropdown
const switcherOpen = ref(false);
const switcherRef = ref(null);
onClickOutside(switcherRef, () => {
  switcherOpen.value = false;
});

const toggleSwitcher = () => {
  if (!hasMultipleAccounts.value) return;
  switcherOpen.value = !switcherOpen.value;
};

const inboxBadge = computed(() => mineCount.value);
</script>

<template>
  <aside class="il-side">
    <div class="il-brand">
      <!-- Real InteliChat mark (staged asset) - never redrawn. Full
           wordmark on the open rail, bubble icon when collapsed. -->
      <img
        class="il-brand-full il-brand-light"
        :src="logoFull"
        alt="InteliChat by Next Level"
      />
      <img
        class="il-brand-full il-brand-dark"
        :src="logoDark"
        alt="InteliChat by Next Level"
      />
      <img class="il-brand-icon" :src="logoIcon" alt="InteliChat" />
    </div>

    <!-- account switcher (real accounts, full-reload switch like native) -->
    <div ref="switcherRef" class="il-acct">
      <button
        class="il-acct-btn"
        :class="{ switchable: hasMultipleAccounts, open: switcherOpen }"
        :data-account-id="accountId"
        data-il="account-switcher"
        @click="toggleSwitcher"
      >
        <span class="nm">{{ accountName }}</span>
        <svg
          v-if="hasMultipleAccounts"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      <div v-if="switcherOpen" class="il-acct-menu" data-il="account-menu">
        <div class="h">Switch account</div>
        <button
          v-for="account in sortedAccounts"
          :key="account.id"
          class="it"
          :class="{ on: account.id === accountId }"
          :data-il-account="account.id"
          @click="switchAccount(account.id)"
        >
          <span class="nm">{{ account.name }}</span>
          <span class="rl">{{ account.role }}</span>
        </button>
      </div>
    </div>

    <nav class="il-navwrap">
      <AttentionQueueBadge />
      <button
        v-for="item in WORKSPACES"
        :key="item.key"
        class="il-nav"
        :class="{ on: isActive(item.key) }"
        :data-il-nav="item.key"
        @click="goTo(item.routeName)"
      >
        <svg
          v-if="item.key === 'today'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
        </svg>
        <svg
          v-else-if="item.key === 'inbox'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path
            d="M21 11.5a8.5 8.5 0 0 1-8.5 8.3 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 1 1 17 0z"
          />
        </svg>
        <svg
          v-else-if="item.key === 'history'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 7v5l3.5 2" />
        </svg>
        <svg
          v-else-if="item.key === 'customers'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <circle cx="9" cy="8" r="4" />
          <path
            d="M2 21a7 7 0 0 1 14 0M17 11a4 4 0 0 0 0-6M22 21a6 6 0 0 0-4-5.6"
          />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <path d="M3 6h18M16 10a4 4 0 0 1-8 0" />
        </svg>
        <span class="lbl">{{ item.label }}</span>
        <span v-if="item.key === 'inbox' && inboxBadge" class="n il-mono">
          {{ inboxBadge }}
        </span>
      </button>

      <div class="il-nav-h">Tools</div>
      <button
        v-for="item in TOOLS"
        :key="item.key"
        class="il-nav"
        :class="{ on: isActive(item.key) }"
        :data-il-nav="item.key"
        @click="goTo(item.routeName)"
      >
        <svg
          v-if="item.key === 'fitment'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <circle cx="12" cy="12" r="3" />
          <path
            d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2"
          />
        </svg>
        <svg
          v-else-if="item.key === 'recovery'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
        </svg>
        <svg
          v-else-if="item.key === 'tickets'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path d="M4 5h16a1 1 0 0 1 1 1v3a2 2 0 0 0 0 4v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a2 2 0 0 0 0-4V6a1 1 0 0 1 1-1z" />
          <path d="M13 5v14" stroke-dasharray="2 2" />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path d="M3 3v18h18M18 17V9M13 17V5M8 17v-3" />
        </svg>
        <span class="lbl">{{ item.label }}</span>
      </button>

      <div class="il-nav-h">Manage</div>
      <template v-for="item in NATIVE_SECTIONS" :key="item.key">
        <button
          class="il-nav"
          :class="{ on: isNativeActive(item) }"
          :data-il-nav="item.key"
          @click="goToNative(item)"
        >
          <svg
            v-if="item.key === 'contacts'"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          >
            <circle cx="12" cy="7" r="4" />
            <path d="M4 21a8 8 0 0 1 16 0" />
          </svg>
          <svg
            v-else-if="item.key === 'reports'"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          >
            <path d="M21 12a9 9 0 1 1-9-9" />
            <path d="M12 3a9 9 0 0 1 9 9h-9z" />
          </svg>
          <svg
            v-else-if="item.key === 'notifications'"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          >
            <path
              d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"
            />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          >
            <circle cx="12" cy="12" r="3" />
            <path
              d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5h.1a1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"
            />
          </svg>
          <span class="lbl">{{ item.label }}</span>
        </button>
        <div
          v-if="item.children && isNativeActive(item)"
          class="il-subnav"
          :data-il-subnav="item.key"
        >
          <button
            v-for="c in item.children"
            :key="c.routeName"
            class="il-subnav-it"
            :class="{ on: isSubActive(c) }"
            @click="goToNative(c)"
          >
            {{ c.label }}
          </button>
        </div>
      </template>

      <div class="il-nav-h">Chatwoot</div>
      <button class="il-nav" data-il-nav="native" @click="openNative">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <path d="M3 9h18M9 21V9" />
        </svg>
        <span class="lbl">Classic view</span>
      </button>
    </nav>

    <div class="il-side-sp" />

    <!-- agent identity -->
    <div class="il-me" data-il="agent-identity">
      <span class="av">{{ initials }}</span>
      <div class="b">
        <div class="nm">{{ displayName }}</div>
        <div class="rl">
          <i :class="availabilityTone" />{{ availabilityLabel }}
        </div>
      </div>
    </div>
  </aside>
</template>

<style lang="scss" scoped>
.il-side {
  /* metallic right edge (1px sheen strip) + faint silver wash toward the
     rail's foot - the corporate-cyberpunk premium layer. Background layers
     stay pinned to the element box, so the edge survives rail scrolling. */
  background:
    linear-gradient(
        180deg,
        var(--il-silver-2) 0%,
        var(--il-silver) 28%,
        var(--il-silver-soft) 52%,
        var(--il-silver) 78%,
        var(--il-silver-2) 100%
      )
      right / 1px 100% no-repeat,
    linear-gradient(
      180deg,
      var(--il-card) 0%,
      var(--il-card) 58%,
      var(--il-silver-soft) 100%
    );
  display: flex;
  flex-direction: column;
  padding: 18px 14px;
  min-height: 0;
  overflow-y: auto;
}

.il-brand {
  display: flex;
  align-items: center;
  padding: 6px 8px 18px;

  .il-brand-full {
    display: block;
    width: 100%;
    max-width: 196px;
    height: auto;
  }

  .il-brand-icon {
    display: none;
    width: 34px;
    height: auto;
  }
}

.il-acct {
  position: relative;
  margin-bottom: 10px;

  .il-acct-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border-radius: var(--il-r-s);
    border: 1px solid var(--il-line);
    text-align: left;

    .nm {
      flex: 1;
      font-size: 12.5px;
      font-weight: 600;
      color: var(--il-ink-2);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    svg {
      width: 14px;
      height: 14px;
      color: var(--il-ink-3);
      flex-shrink: 0;
    }

    &.switchable {
      cursor: pointer;

      &:hover,
      &.open {
        background: var(--il-paper);
        border-color: var(--il-line-2);
      }
    }
  }

  .il-acct-menu {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    background: var(--il-card);
    border: 1px solid var(--il-line-2);
    border-radius: var(--il-r-s);
    box-shadow: var(--il-sh-lg);
    padding: 6px;
    z-index: 40;

    .h {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--il-ink-3);
      padding: 6px 8px;
    }

    .it {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      text-align: left;
      padding: 8px 9px;
      border-radius: 8px;

      .nm {
        flex: 1;
        font-size: 13px;
        font-weight: 500;
        color: var(--il-ink);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .rl {
        font-size: 11px;
        color: var(--il-ink-3);
        text-transform: capitalize;
      }

      &:hover {
        background: var(--il-paper);
      }

      &.on .nm {
        color: var(--il-accent);
        font-weight: 600;
      }
    }
  }
}

.il-nav-h {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--il-ink-4);
  padding: 16px 10px 7px;
}

.il-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 11px;
  border-radius: var(--il-r-s);
  color: var(--il-ink-2);
  font-weight: 500;
  font-size: 13.5px;
  width: 100%;
  text-align: left;
  transition: 0.12s;

  svg {
    width: 18px;
    height: 18px;
    color: var(--il-ink-3);
    flex-shrink: 0;
  }

  .lbl {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .n {
    font-size: 11px;
    color: var(--il-ink-3);
  }

  &:hover {
    background: var(--il-paper);
    color: var(--il-ink);
  }

  &.on {
    background: var(--il-accent-soft);
    color: var(--il-accent);
    font-weight: 600;

    svg,
    .n {
      color: var(--il-accent);
    }
  }
}

.il-subnav {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 2px 0 6px 34px;

  .il-subnav-it {
    text-align: left;
    font-size: 12.5px;
    color: var(--il-ink-3);
    padding: 5px 8px;
    border-radius: 7px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      background: var(--il-paper);
      color: var(--il-ink);
    }

    &.on {
      color: var(--il-accent);
      font-weight: 600;
      background: var(--il-accent-soft);
    }
  }
}

.il-side-sp {
  flex: 1;
  min-height: 12px;
}

.il-me {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px;
  border-radius: var(--il-r-s);
  border: 1px solid var(--il-line);

  .av {
    width: 38px;
    height: 38px;
    font-size: 13px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-weight: 600;
    flex-shrink: 0;
    font-family: var(--il-fd);
    background: var(--il-accent-soft);
    color: var(--il-accent);
  }

  .b {
    min-width: 0;
  }

  .nm {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    color: var(--il-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rl {
    font-size: 11.5px;
    color: var(--il-ink-3);
    display: flex;
    align-items: center;
    gap: 5px;

    i {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--il-ink-4);
      display: inline-block;

      &.good {
        background: var(--il-good);
      }
      &.warn {
        background: var(--il-warn);
      }
    }
  }
}

/* narrow shells: icon-only rail */
@media (max-width: 900px) {
  .il-side {
    padding: 14px 8px;
  }

  .il-brand {
    justify-content: center;
    padding: 4px 0 14px;

    .il-brand-full {
      display: none;
    }

    .il-brand-icon {
      display: block;
    }
  }

  .il-acct,
  .il-nav .lbl,
  .il-nav .n,
  .il-nav-h,
  .il-subnav,
  .il-me .b {
    display: none;
  }

  .il-nav {
    justify-content: center;
    padding: 10px 0;
  }

  .il-me {
    justify-content: center;
    padding: 6px;
    border: none;
  }
}
</style>

<style lang="scss">
/* UNSCOPED on purpose (namespaced under .il-app, leaks nowhere): the
   theme class lives on <body>, which scoped selectors cannot reach.
   Light theme shows the near-black wordmark, dark theme the white one. */
.il-app .il-side .il-brand .il-brand-dark {
  display: none;
}

body.dark .il-app .il-side .il-brand .il-brand-light {
  display: none;
}

body.dark .il-app .il-side .il-brand .il-brand-dark {
  display: block;
  width: 100%;
  max-width: 196px;
  height: auto;
}

/* collapsed rail (<900px): only the bubble icon, in either theme */
@media (max-width: 900px) {
  body.dark .il-app .il-side .il-brand .il-brand-dark {
    display: none;
  }
}
</style>
